import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';

const Login: React.FC = () => {
  const { signInWithMicrosoft } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleMicrosoftSignIn = async () => {
    try {
      setLoading(true);
      setError(null);
      await signInWithMicrosoft();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred during sign in');
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: '#CCBFAF',
        fontFamily: "'Inter', 'Roboto', Arial, sans-serif",
      }}
    >
      <Card
        sx={{
          backgroundColor: '#fff',
          borderRadius: '12px',
          boxShadow: '0 10px 40px rgba(117, 110, 97, 0.2)',
          padding: '48px',
          width: '100%',
          maxWidth: '420px',
          minWidth: '420px',
          textAlign: 'center',
          border: '1px solid rgba(204, 191, 175, 0.3)',
        }}
      >
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ mb: 3 }}>
            <Typography 
              variant="h1" 
              component="h1" 
              sx={{ 
                fontSize: '2.2rem',
                fontWeight: 700,
                color: '#756E61',
                margin: '0 0 8px 0',
                letterSpacing: '-0.02em',
                fontFamily: "'Inter', 'Roboto', Arial, sans-serif",
              }}
            >
              DM Chirurgie
            </Typography>
            <Typography 
              component="p" 
              sx={{ 
                fontSize: '1.1rem',
                color: '#756E61',
                margin: '0 0 36px 0',
                fontWeight: 500,
                fontFamily: "'Inter', 'Roboto', Arial, sans-serif",
              }}
            >
              Forms
            </Typography>
          </Box>

          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                backgroundColor: '#fef2f2',
                color: '#dc2626',
                border: '1px solid #fecaca',
                borderRadius: '8px',
                padding: '12px 16px',
                marginBottom: '24px',
                fontSize: '0.95rem',
                fontWeight: 500,
                '& .MuiAlert-message': {
                  color: '#dc2626',
                },
              }}
            >
              {error}
            </Alert>
          )}

          <Button
            fullWidth
            variant="contained"
            onClick={handleMicrosoftSignIn}
            disabled={loading}
            sx={{
              backgroundColor: '#756E61',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              padding: '16px 24px',
              fontSize: '1.1rem',
              fontWeight: 600,
              cursor: 'pointer',
              width: '100%',
              transition: 'all 0.2s ease',
              marginBottom: '24px',
              boxShadow: '0 4px 12px rgba(117, 110, 97, 0.3)',
              fontFamily: "'Inter', 'Roboto', Arial, sans-serif",
              textTransform: 'none',
              '&:hover:not(:disabled)': {
                backgroundColor: '#CCBFAF',
                color: '#756E61',
                transform: 'translateY(-1px)',
                boxShadow: '0 6px 16px rgba(117, 110, 97, 0.4)',
              },
              '&:disabled': {
                backgroundColor: '#CCBFAF',
                cursor: 'not-allowed',
                transform: 'none',
                boxShadow: 'none',
              },
            }}
          >
            {loading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} color="inherit" />
                Inloggen...
              </Box>
            ) : (
              'Inloggen met Microsoft'
            )}
          </Button>

          <Typography 
            variant="body2" 
            sx={{ 
              fontSize: '0.9rem',
              color: '#756E61',
              margin: 0,
              lineHeight: 1.5,
              fontFamily: "'Inter', 'Roboto', Arial, sans-serif",
            }}
          >
            Gebruik uw Microsoft account om toegang te krijgen tot het systeem
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Login;